import React from 'react';
import type { CSSProperties } from 'react';
import {
    ICON_COLORS,
    HomeIcon,
    DashboardIcon,
    TeacherIcon,
    StudentIcon,
    ClassesIcon,
    BulkUploadIcon,
    OrganizationsIcon,
    UsersIcon,
    QuestionBankIcon,
    ExamsIcon,
    ReportsIcon,
    EditorIcon,
    HandscoreIcon,
    LogoutIcon,
    VolumeIcon,

    // New Icons
    AnnouncementIcon,
    PdfIcon,
    FilterIcon,
    TrashIcon,
    PreviewIcon,
    DuplicateIcon,
    EditIcon,
    ActivateIcon,
    DeactivateIcon,
    ResetPasswordIcon,
    ActivityLogIcon,
    SortIcon,
    CheckIcon,
    XIcon,
    SyncIcon,
    UserIcon,
    UsernameIcon,
    GradeLevelIcon,
} from '@/src/ui';

// Local type for props
type SVGIconProps = {
    color?: string;
    secondaryColor?: string;
    style?: React.CSSProperties;
};

type IconEntry = {
    label: string;
    Icon: (props: SVGIconProps) => React.ReactNode;
};

const ICONS: IconEntry[] = [
    { label: 'Home', Icon: HomeIcon },
    { label: 'Dashboard', Icon: DashboardIcon },
    { label: 'Teachers', Icon: TeacherIcon },
    { label: 'Students', Icon: StudentIcon },
    { label: 'Classes', Icon: ClassesIcon },
    { label: 'Bulk Upload', Icon: BulkUploadIcon },
    { label: 'School/District', Icon: OrganizationsIcon },
    { label: 'Admin/Users', Icon: UsersIcon },
    { label: 'Question Bank', Icon: QuestionBankIcon },
    { label: 'Exams', Icon: ExamsIcon },
    { label: 'Exam/Question Editor', Icon: EditorIcon },
    { label: 'Reports', Icon: ReportsIcon },
    { label: 'Handscore', Icon: HandscoreIcon },
    { label: 'Volume', Icon: VolumeIcon },
    { label: 'Logout', Icon: LogoutIcon },
    { label: 'Announcement', Icon: AnnouncementIcon },
    { label: 'PDF', Icon: PdfIcon },
    { label: 'Filter', Icon: FilterIcon },
    { label: 'Trash', Icon: TrashIcon },
    { label: 'Preview', Icon: PreviewIcon },
    { label: 'Duplicate', Icon: DuplicateIcon },
    { label: 'Edit', Icon: EditIcon },
    { label: 'Activate', Icon: ActivateIcon },
    { label: 'Deactivate', Icon: DeactivateIcon },
    { label: 'Reset Password', Icon: ResetPasswordIcon },
    { label: 'Activity Log', Icon: ActivityLogIcon },
    { label: 'Sort', Icon: SortIcon },
    { label: 'Check', Icon: CheckIcon },
    { label: 'X', Icon: XIcon },
    { label: 'Sync', Icon: SyncIcon },
    { label: 'User', Icon: UserIcon },
    { label: 'Username', Icon: UsernameIcon },
    { label: 'Grade Level', Icon: GradeLevelIcon },
];

export type IconLegendProps = {
    color?: string;
    size?: number;
    columns?: number;
    style?: CSSProperties;
};

export default function IconLegend({
    color = ICON_COLORS.primary,
    size = 28,
    columns = 2,
    style,
}: IconLegendProps) {
    const iconStyle: CSSProperties = { width: size, height: size };

    return (
        <div
            style={{
                border: '1px solid #ddd',
                borderRadius: 6,
                padding: 16,
                backgroundColor: '#fff',
                fontSize: 14,
                ...style,
            }}
        >
            <h4 style={{ marginBottom: 12, fontWeight: 'bold' }}>Icon Legend</h4>
            <div
                style={{
                    display: 'grid',
                    gridTemplateColumns: `repeat(${columns}, minmax(180px, 1fr))`,
                    gap: 12,
                    alignItems: 'center',
                }}
            >
                {ICONS.map(({ label, Icon }) => (
                    <div
                        key={label}
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 8,
                        }}
                    >
                        <Icon color={color} style={iconStyle} />
                        <span>{label}</span>
                    </div>
                ))}
            </div>
        </div>
    );
}
